import { Card, Flex, Grid, Input, Text } from "@mantine/core";
import { DateInput, DateTimePicker } from "@mantine/dates";
import dayjs from "dayjs";
import NaCheckbox from "./NaCheckbox";
import { StyledCheckbox } from "../../../../components/shared/_FormInputs/CheckboxGroup/CheckboxGroup.styled";
import { useTheme } from "../../../../hooks";
import { type ICheckList, ICheckListItem } from "../../../../interfaces";
import { type ChangeEvent, useCallback } from "react";
import { useSelector } from "src/redux/hooks";
import MultiLineText from "../../../../components/shared/MultiLine/MultiLineText";
interface Props {
  itemIndex: number;
  item: ICheckListItem;
  setSelecedCheckList: (v: any) => void;
  selectedItemIndex: number | null;
  disabled: boolean | undefined;
  reverseNaOrder?: boolean;
}
const CheckListsFormItem = ({
  itemIndex,
  item,
  setSelecedCheckList,
  selectedItemIndex,
  disabled,
  reverseNaOrder,
}: Props) => {
  const theme = useTheme();
  const uppercase = useSelector(state => state.preferencesSlice.uppercase);
  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, isNaInput?: boolean) => {
      setSelecedCheckList((prev: ICheckList) => {
        const data = structuredClone(prev.data);
        let list = data.list;

        if (selectedItemIndex !== null)
          list = (list[selectedItemIndex] as any).items;

        const index = Number(event.target.name);
        if (index !== -1) {
          if (isNaInput) {
            list[index].notAvailable = event.target.checked;
            list[index].naChanged = true;
          } else {
            list[index].value =
              list[index].unit === "text"
                ? event.target.value
                : event.target.checked;
            list[index].changed = true;
          }
          list[index].timestamp = new Date().getTime();
        }
        if (selectedItemIndex !== null)
          (data.list[selectedItemIndex] as any).items = list;
        else data.list = list;

        return { ...prev, data };
      });
    },
    [selectedItemIndex],
  );

  const handleDateChange = useCallback(
    (date: Date | null, itemIndex: number) => {
      setSelecedCheckList((prev: ICheckList) => {
        const data = structuredClone(prev.data);
        let list = data.list;

        if (selectedItemIndex !== null)
          list = (list[selectedItemIndex] as any).items;

        if (itemIndex !== -1) {
          list[itemIndex].value = date ? dayjs(date).toISOString() : "";
          list[itemIndex].changed = true;
          list[itemIndex].timestamp = new Date().getTime();
        }

        if (selectedItemIndex !== null)
          (data.list[selectedItemIndex] as any).items = list;
        else data.list = list;

        return { ...prev, data };
      });
    },
    [selectedItemIndex],
  );

  const handleMultiChange = useCallback(
    (subFieldIndex: number, val: any, isNaInput?: boolean) => {
      setSelecedCheckList((prev: ICheckList) => {
        const data = structuredClone(prev.data);
        let list = data.list;

        if (selectedItemIndex !== null)
          list = (list[selectedItemIndex] as any).items;

        const index = itemIndex; // Use prop directly
        if (index !== -1) {
          if (list[index].fields && list[index].fields[subFieldIndex]) {
            if (isNaInput) {
              list[index].fields[subFieldIndex].notAvailable = val;
              list[index].fields[subFieldIndex].naChanged = true;
            } else {
              list[index].fields[subFieldIndex].value = val;
              list[index].fields[subFieldIndex].changed = true;
            }
          }
          list[index].changed = true;
          list[index].timestamp = new Date().getTime();
        }

        if (selectedItemIndex !== null)
          (data.list[selectedItemIndex] as any).items = list;
        else data.list = list;
        return { ...prev, data };
      });
    },
    [selectedItemIndex, itemIndex],
  );

  const renderSubFields = useCallback((field: any, fieldIndex: number) => {
    const val = field.value;
    if (field.unit === "check") {
      return (
        <div
          key={field.id || fieldIndex}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}>
          <Text size="sm" color="text">
            {field.text}
          </Text>
          <div
            style={{
              height: 36,
              display: "flex",
              alignItems: "center",
            }}>
            <StyledCheckbox
              size="md"
              checked={!!val}
              onChange={e =>
                handleMultiChange(fieldIndex, e.currentTarget.checked)
              }
              disabled={disabled || field.notAvailable}
            />
          </div>
        </div>
      );
    }
    if (field.unit === "date" || field.unit === "datetime") {
      const time = field.unit === "datetime";
      return (
        <div
          key={field.id || fieldIndex}
          style={{ maxWidth: time ? 205 : 140, width: "100%" }}>
          <Text size="sm" color="text">
            {field.text}
          </Text>
          {time ? (
            /*//@ts-ignore */
            <DateTimePicker
              value={typeof val === "string" && val ? new Date(val) : null}
              onChange={(date: Date | null) =>
                handleMultiChange(
                  fieldIndex,
                  date ? dayjs(date).toISOString() : "",
                )
              }
              valueFormat="DD MMM YYYY - HH:mm LT"
              disabled={disabled || field.notAvailable}
              popoverProps={{ withinPortal: true }}
              clearable
              placeholder="Select date/time"
              w="100%"
            />
          ) : (
            <DateInput
              value={typeof val === "string" && val ? new Date(val) : null}
              onChange={(date: Date | null) =>
                handleMultiChange(
                  fieldIndex,
                  date ? dayjs(date).toISOString() : "",
                )
              }
              valueFormat="DD MMM YYYY"
              disabled={disabled || field.notAvailable}
              popoverProps={{ withinPortal: true }}
              clearable
              placeholder="Select date"
              w="100%"
            />
          )}
        </div>
      );
    }
    return (
      <div key={field.id || fieldIndex} style={{ flex: 1, minWidth: "150px" }}>
        <Text size="sm" color="text">
          {field.text}
        </Text>
        <Input
          value={val || ""}
          onChange={e => handleMultiChange(fieldIndex, e.target.value)}
          disabled={disabled}
          style={{
            width: "100%",
          }}
          styles={{
            input: {
              textTransform: uppercase ? "uppercase" : "none",
            },
          }}
        />
      </div>
    );
  }, []);

  switch (item.unit) {
    case "multiple":
      return (
        <Card p={0} shadow="md" radius="md" withBorder>
          <Grid align="center" pr={5}>
            <Grid.Col span={4}>
              <Text
                p={10}
                color={
                  theme?.colors?.text && theme?.colors?.text[0]
                    ? theme?.colors?.text[0]
                    : ""
                }>
                <MultiLineText text={item.text} />
              </Text>
            </Grid.Col>
            <Grid.Col span={8}>
              <Flex
                direction="row"
                gap="lg"
                wrap="wrap"
                align="flex-end"
                justify="flex-end"
                py={5}>
                {(item.fields || []).map((field: any, fieldIndex: number) => (
                  <Flex align="flex-end">
                    {renderSubFields(field, fieldIndex)}
                    {field.naAvailable && (
                      <StyledCheckbox
                        key={`na-${field.id || fieldIndex}`}
                        checked={!!field.notAvailable}
                        onChange={e =>
                          handleMultiChange(fieldIndex, e.target.checked, true)
                        }
                        label={
                          <Text size="sm" style={{ textWrap: "nowrap" }}>
                            N/A
                          </Text>
                        }
                        labelPosition="left"
                        disabled={disabled}
                        size="md"
                        styles={{
                          body: {
                            display: "flex",
                            alignItems: "center",
                            padding: "6px 12px 6px 4px !important",
                          },
                          label: {
                            paddingRight: "8px",
                          },
                        }}
                      />
                    )}
                  </Flex>
                ))}
              </Flex>
            </Grid.Col>
          </Grid>
        </Card>
      );
    case "check":
      return (
        <Card
          p={0}
          shadow="md"
          radius="md"
          withBorder
          style={{ display: "flex", alignItems: "center" }}>
          <Text
            p={10}
            color={
              theme?.colors?.text && theme?.colors?.text[0]
                ? theme?.colors?.text[0]
                : ""
            }
            style={{ width: "100%" }}>
            <MultiLineText text={item.text} />
          </Text>
          <div
            style={{
              display: "flex",
              flexFlow: reverseNaOrder ? "row-reverse" : "row",
            }}>
            {item.naAvailable && (
              <NaCheckbox
                checked={item.notAvailable}
                onChange={e => handleInputChange(e, true)}
                index={itemIndex}
                disabled={disabled}
              />
            )}

            <StyledCheckbox
              w="100%"
              size="md"
              labelPosition="left"
              label={reverseNaOrder ? "Yes" : undefined}
              key={`check-${itemIndex}`}
              checked={typeof item.value === "boolean" ? item.value : false}
              name={String(itemIndex)}
              onChange={handleInputChange}
              disabled={disabled || item.notAvailable}
              style={{ flex: 1 }}
            />
          </div>
        </Card>
      );
    case "text":
      return (
        <Card p={0} key={item.text} shadow="md" radius="md" withBorder>
          <Grid align="center" pr={0}>
            <Grid.Col span={4}>
              <Text
                p={10}
                color={
                  theme?.colors?.text && theme?.colors?.text[0]
                    ? theme?.colors?.text[0]
                    : ""
                }>
                <MultiLineText text={item.text} />
              </Text>
            </Grid.Col>
            <Grid.Col
              span={8}
              display="flex"
              style={{
                alignItems: "center",
              }}>
              <Input
                key={`text-${itemIndex}`}
                value={
                  typeof item.value !== "boolean" ? item.value?.toString() : ""
                }
                py={5}
                pr={!!item.naAvailable ? 0 : 5}
                style={{
                  flex: 1,
                }}
                styles={{
                  input: {
                    textTransform: uppercase ? "uppercase" : "none",
                  },
                }}
                name={String(itemIndex)}
                onChange={handleInputChange}
                disabled={disabled || item.notAvailable}
              />
              {item.naAvailable && (
                <Flex>
                  <NaCheckbox
                    checked={item.notAvailable}
                    onChange={e => handleInputChange(e, true)}
                    index={itemIndex}
                    disabled={disabled}
                  />
                </Flex>
              )}
            </Grid.Col>
          </Grid>
        </Card>
      );
    case "date":
    case "datetime":
      const time = item.unit === "datetime";
      return (
        <Card p={0} key={item.text} shadow="md" radius="md" withBorder>
          <Grid align="center">
            <Grid.Col span={4}>
              <Text
                p={10}
                color={
                  theme?.colors?.text && theme?.colors?.text[0]
                    ? theme?.colors?.text[0]
                    : ""
                }>
                <MultiLineText text={item.text} />
              </Text>
            </Grid.Col>
            <Grid.Col
              span={8}
              display="flex"
              style={{ alignItems: "center", justifyContent: "flex-end" }}>
              {time ? (
                /*//@ts-ignore */
                <DateTimePicker
                  py={5}
                  maw={205}
                  w="100%"
                  pr={!!item.naAvailable ? 0 : 5}
                  key={`date-${itemIndex}`}
                  value={
                    typeof item.value === "string" && item.value
                      ? new Date(item.value)
                      : null
                  }
                  onChange={date => handleDateChange(date, itemIndex)}
                  valueFormat="DD MMM YYYY - HH:mm LT"
                  disabled={disabled || item.notAvailable}
                  popoverProps={{ withinPortal: true }}
                  placeholder="Select date/time"
                  clearable
                />
              ) : (
                <DateInput
                  py={5}
                  maw={140}
                  w="100%"
                  pr={!!item.naAvailable ? 0 : 5}
                  key={`date-${itemIndex}`}
                  value={
                    typeof item.value === "string" && item.value
                      ? new Date(item.value)
                      : null
                  }
                  onChange={(date: Date | null) =>
                    handleDateChange(date, itemIndex)
                  }
                  valueFormat="DD MMM YYYY"
                  disabled={disabled || item.notAvailable}
                  popoverProps={{ withinPortal: true }}
                  placeholder="Select date"
                  clearable
                />
              )}
              {item.naAvailable && (
                <Flex>
                  <NaCheckbox
                    checked={item.notAvailable}
                    onChange={e => handleInputChange(e, true)}
                    index={itemIndex}
                    disabled={disabled}
                  />
                </Flex>
              )}
            </Grid.Col>
          </Grid>
        </Card>
      );
    default:
      return (
        <Card
          p={0}
          key={item.text}
          shadow="md"
          radius="md"
          withBorder
          bg={theme.colors.tdBackgroundColor[0]}>
          <Text
            p={10}
            color={
              theme?.colors?.text && theme?.colors?.text[0]
                ? theme?.colors?.text[0]
                : ""
            }
            weight={200}>
            <MultiLineText text={item.text} />
          </Text>
        </Card>
      );
  }
};
export default CheckListsFormItem;
